<template>
  <LennocModal domClass="modal--lightbox">
    <div v-if="imgSource.length > 0" class="aspect-ratio aspect-ratio--16-9">
      <img v-if="!isPdf" :src="imgSource" class="lightbox__image" />
      <iframe v-else :src="imgSource"></iframe>
    </div>
    <div v-if="!busy" class="lightbox__controls">
      <a class="link link--edit"
        v-if="isRemoteFile"
        @click.prevent="downloadFile($event)">
        <LennocIcon image="download" />
        <span>{{ popUpStrings.buttons.download }}</span>
      </a>
      <a class="link link--edit" @click.prevent="$emit('close')">
        <LennocIcon image="squared-cross" />
        <span>{{ popUpStrings.buttons.close }}</span>
      </a>
    </div>
    <LennocSpinner v-show="busy" isFullscreen="false" />
  </LennocModal>
</template>

<script lang="ts" setup>
  import { onMounted, PropType, ref, watch } from 'vue'
  import { FileObject } from '../../types/types'

  const props = defineProps({
    document: {
      type: Object as PropType<FileObject>,
      required: true
    },
    downloadedFile: {
      type: Object,
      default: {}
    },
    popUpStrings: {
      type: Object,
      required: true
    },
    secret: {
      type: String,
      default: ''
    }
  })

  const emits = defineEmits([
    'close',
    'download'
  ])

  onMounted(async () => {
    busy.value = true
    if (props.document.Id || props.document.FileId) {
      getDocumentFile()
    } else {
      try {
        const fileSource = await readAsDataURL(props.document.file)
        isPdf.value = !!fileSource.includes('/pdf')
        imgSource.value = fileSource
        isRemoteFile.value = false
        busy.value = false
      } catch (error) {
        imgSource.value = ''
        busy.value = false
      }
    }
  })

  watch(() => props.downloadedFile, () => {
    if (props.downloadedFile) {
      showFile()
    }
  })

  const busy = ref(false)
  const imgSource = ref('')
  const isPdf = ref(false)
  const isRemoteFile = ref(false)

  const getDocumentFile = async () => {
    try {
      const downloadFilePayload = {
        fileId: props.document.FileId || props.document.Id,
        fileType: props.document.kind || props.document.ContentType || props.document.FileType,
        fileName: props.document.FileName || props.document.Name,
        secret: props.secret
      }
      downloadFile(downloadFilePayload)
    } catch (error) {
      emits('close', error)
    }
  }

  const downloadFile = (payload : Record<string, any>) => {
    !payload.isTrusted
      ? emits('download', { ...payload, downloadFromServer: true })
      : emits('download', {
          imgSource: imgSource.value,
          imgName: props.document.name || props.document.FileName
        })    
  }

  const readAsDataURL = (inputFile : File) => {
    const temporaryFileReader = new FileReader()
    const dataUrl = new Promise((resolve, reject) => {
      temporaryFileReader.onerror = () => {
        temporaryFileReader.abort()
        reject(new Error())
      }
      temporaryFileReader.onload = () => {
        resolve(temporaryFileReader.result)
      }
      temporaryFileReader.readAsDataURL(inputFile)
    })
    return dataUrl as Promise<string>
  }

  const showFile = () => {
    isPdf.value = !!props.downloadedFile.data.type?.includes('/pdf')
    const url = URL.createObjectURL(props.downloadedFile.data)
    imgSource.value = url
    isRemoteFile.value = true
    busy.value = false
  }

</script>